import { useState, useCallback } from 'react';
import { data_from_server_post } from '@/shared/http/client';
/**
 * Custom hook for managing benchmark-external model predictions validation
 */
export const useBenchmarkValidation = () => {
    const [validationState, setValidationState] = useState({
        isValidating: false,
        validationResult: null,
        validationError: null,
    });

    /**
     * Validate that examples in benchmarks match with external model predictions
     * @param {string} clientId - The client identifier
     * @param {string} llmUsecaseId - The usecase identifier
     * @param {string[]} benchmarkNames - Array of benchmark names
     * @param {string} externalPredictionsName - Name of external predictions dataset
     * @returns {Promise<object>} Validation result
     */
    const validateBenchmarkMatch = useCallback(
        async (clientId, llmUsecaseId, benchmarkNames, externalPredictionsName) => {
            if (!externalPredictionsName) {
                setValidationState({
                    isValidating: false,
                    validationResult: null,
                    validationError: null,
                });
                return null;
            }

            setValidationState({
                isValidating: true,
                validationResult: null,
                validationError: null,
            });

            return new Promise((resolve) => {
                data_from_server_post(
                    'validate-benchmark-external-match',
                    {
                        client_id: clientId,
                        llm_usecase_id: llmUsecaseId,
                        benchmark_names: benchmarkNames,
                        external_predictions_name: externalPredictionsName,
                    },
                    (response) => {
                        if (response?.success) {
                            setValidationState({
                                isValidating: false,
                                validationResult: response,
                                validationError: null,
                            });
                            resolve(response);
                        } else {
                            setValidationState({
                                isValidating: false,
                                validationResult: null,
                                validationError: response?.error || 'Validation failed',
                            });
                            resolve(null);
                        }
                    },
                    (error) => {
                        setValidationState({
                            isValidating: false,
                            validationResult: null,
                            validationError: error || 'Network error during validation',
                        });
                        resolve(null);
                    }
                );
            });
        },
        []
    );

    const clearValidation = useCallback(() => {
        setValidationState({
            isValidating: false,
            validationResult: null,
            validationError: null,
        });
    }, []);

    return {
        ...validationState,
        validateBenchmarkMatch,
        clearValidation,
    };
};

